// ArticleSocialBar — 文章详情底部社交栏(pen ExFCc;2026-06-12 spec §5.1)。
// 固定悬浮(fixed bottom);4 pill:喜欢/不喜欢/打赏/评论。icon = 设计稿 lucide。
// shared SocialBar 不适用(pill 布局 vs 图标列布局),pack 场景零影响。

import type { ReactNode } from 'react'
import type { ArticleSocialStats } from '../_lib/social-api'

type Props = {
  stats: ArticleSocialStats | null
  isPending?: boolean
  onLike: () => void
  onDislike: () => void
  onReward: () => void
  onComment: () => void
}

const fmt = (n: number | undefined | null): string => (n == null ? '—' : String(n))

export function ArticleSocialBar({ stats, isPending, onLike, onDislike, onReward, onComment }: Props) {
  const liked = stats?.i_liked === true
  const disliked = stats?.i_disliked === true
  return (
    <div
      data-testid="article-social-bar"
      className="fixed inset-x-0 bottom-0 z-40 flex justify-center gap-3 border-t border-[#F3F4F6] bg-white px-5 pt-3 shadow-[0_-2px_12px_rgba(0,0,0,0.06)]"
      style={{ paddingBottom: 'max(16px, env(safe-area-inset-bottom))' }}
    >
      <Pill
        testid="art-like"
        active={liked}
        activeCls="bg-[#F0FBF9] text-[#19caad]"
        idleCls="bg-[#F6F7F8] text-[#6B7280]"
        disabled={isPending}
        onClick={onLike}
        ariaLabel="喜欢"
      >
        <LucideHeart filled={liked} />
        <span className="text-[14px] font-semibold">{fmt(stats?.like_count)}</span>
      </Pill>
      <Pill
        testid="art-dislike"
        active={disliked}
        activeCls="bg-[#FEF2F2] text-[#EF4444]"
        idleCls="bg-[#F6F7F8] text-[#6B7280]"
        disabled={isPending}
        onClick={onDislike}
        ariaLabel="不喜欢"
      >
        <LucideThumbsDown />
        <span className="text-[14px] font-semibold">{fmt(stats?.dislike_count)}</span>
      </Pill>
      <Pill
        testid="art-reward"
        active={false}
        activeCls=""
        idleCls="bg-[#FFF7ED] text-[#F59E0B]"
        onClick={onReward}
        ariaLabel="打赏"
      >
        <LucideGift />
        <span className="text-[14px] font-semibold">打赏</span>
        <span className="text-[14px] font-semibold">{fmt(stats?.tip_count)}</span>
      </Pill>
      <Pill
        testid="art-comment"
        active={false}
        activeCls=""
        idleCls="bg-[#F6F7F8] text-[#6B7280]"
        onClick={onComment}
        ariaLabel="评论"
      >
        <LucideMessageCircle />
        <span className="text-[14px] font-semibold">{fmt(stats?.comment_count)}</span>
      </Pill>
    </div>
  )
}

function Pill(props: {
  testid: string
  active: boolean
  activeCls: string
  idleCls: string
  disabled?: boolean | undefined
  onClick: () => void
  ariaLabel: string
  children: ReactNode
}) {
  const { testid, active, activeCls, idleCls, disabled, onClick, ariaLabel, children } = props
  return (
    <button
      type="button"
      data-testid={testid}
      aria-label={ariaLabel}
      disabled={disabled}
      onClick={onClick}
      className={`flex items-center gap-2 rounded-full px-4 py-2.5 ${active ? activeCls : idleCls} disabled:opacity-60`}
    >
      {children}
    </button>
  )
}

// lucide 官方 path(设计稿 icon:heart / thumbs-down / gift / message-circle;18px)
const ic = {
  width: 18,
  height: 18,
  viewBox: '0 0 24 24',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
}

function LucideHeart({ filled }: { filled?: boolean }) {
  return (
    <svg {...ic} fill={filled ? 'currentColor' : 'none'}>
      <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z" />
    </svg>
  )
}

function LucideThumbsDown() {
  return (
    <svg {...ic} fill="none">
      <path d="M17 14V2" />
      <path d="M9 18.12 10 14H4.17a2 2 0 0 1-1.92-2.56l2.33-8A2 2 0 0 1 6.5 2H20a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-2.76a2 2 0 0 0-1.79 1.11L12 22a3.13 3.13 0 0 1-3-3.88Z" />
    </svg>
  )
}

function LucideGift() {
  return (
    <svg {...ic} fill="none">
      <rect x="3" y="8" width="18" height="4" rx="1" />
      <path d="M12 8v13" />
      <path d="M19 12v7a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-7" />
      <path d="M7.5 8a2.5 2.5 0 0 1 0-5A4.8 8 0 0 1 12 8a4.8 8 0 0 1 4.5-5 2.5 2.5 0 0 1 0 5" />
    </svg>
  )
}

function LucideMessageCircle() {
  return (
    <svg {...ic} fill="none">
      <path d="M7.9 20A9 9 0 1 0 4 16.1L2 22Z" />
    </svg>
  )
}
